import React, { useState } from 'react'

export interface ComparablesFormState {
  city: string
  district: string
  typology: string
  targetSqm: number
  sqmTolerance: number
  maxListingAgeDays: number
  includeSold: boolean
}

interface Props {
  initial?: Partial<ComparablesFormState>
  onSubmit: (state: ComparablesFormState) => void
  submitting?: boolean
}

const DEFAULTS: ComparablesFormState = {
  city: 'Lisboa',
  district: '',
  typology: 'T2',
  targetSqm: 90,
  sqmTolerance: 15,
  maxListingAgeDays: 365,
  includeSold: true,
}

export function ComparablesForm({ initial, onSubmit, submitting }: Props) {
  const [state, setState] = useState<ComparablesFormState>({ ...DEFAULTS, ...initial })

  const set = <K extends keyof ComparablesFormState>(key: K, value: ComparablesFormState[K]) => {
    setState((prev) => ({ ...prev, [key]: value }))
  }

  return (
    <form
      className="card grid grid-cols-1 md:grid-cols-4 gap-3"
      onSubmit={(e) => {
        e.preventDefault()
        // Strip empty district before sending
        const payload: ComparablesFormState = {
          ...state,
          district: state.district.trim(),
        }
        onSubmit(payload)
      }}
    >
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">City</span>
        <input
          className="input"
          value={state.city}
          onChange={(e) => set('city', e.target.value)}
          required
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">District (optional)</span>
        <input
          className="input"
          value={state.district}
          onChange={(e) => set('district', e.target.value)}
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">Typology</span>
        <input
          className="input"
          value={state.typology}
          onChange={(e) => set('typology', e.target.value.toUpperCase())}
          required
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">Target m²</span>
        <input
          type="number"
          min={10}
          max={2000}
          className="input"
          value={state.targetSqm}
          onChange={(e) => set('targetSqm', Number(e.target.value))}
          required
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">± m² tolerance</span>
        <input
          type="number"
          min={0}
          max={500}
          className="input"
          value={state.sqmTolerance}
          onChange={(e) => set('sqmTolerance', Number(e.target.value))}
        />
      </label>
      <label className="flex flex-col text-sm">
        <span className="text-xs font-medium text-secondary-600 mb-1">Max listing age (days)</span>
        <input
          type="number"
          min={7}
          max={3650}
          className="input"
          value={state.maxListingAgeDays}
          onChange={(e) => set('maxListingAgeDays', Number(e.target.value))}
        />
      </label>
      <label className="flex items-center gap-2 text-sm md:mt-6">
        <input
          type="checkbox"
          checked={state.includeSold}
          onChange={(e) => set('includeSold', e.target.checked)}
        />
        Include recently sold
      </label>
      <div className="flex items-end">
        <button type="submit" className="btn btn-primary w-full md:w-auto" disabled={submitting}>
          {submitting ? 'Searching…' : 'Find comparables'}
        </button>
      </div>
    </form>
  )
}
